import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import {
  catchError,
  defaultIfEmpty,
  map,
  shareReplay,
  tap,
} from "rxjs/operators";
import { ApiService } from "./api.service";
import { UserHelperService } from "./userHelper.service";

export interface FeatureFlags {
  emotionTransfer: boolean; // effective value: global feature flag AND user setting
  emotionTransferFeatureEnabled: boolean; // global feature flag
}

// used when the flags cannot be loaded: the feature stays hidden rather than relying on outdated values
const disabledFlags: FeatureFlags = {
  emotionTransfer: false,
  emotionTransferFeatureEnabled: false,
};

/**
 * The flags stored in the session at login can be outdated (feature disabled after login),
 * so they are fetched again from the API on app load and after each login.
 */
@Injectable({
  providedIn: "root",
})
export class FeatureFlagService {
  private flags$: Observable<FeatureFlags> | null = null;

  constructor(
    private api: ApiService,
    private userHelperService: UserHelperService
  ) {}

  public refresh(): Observable<FeatureFlags> {
    this.flags$ = this.api.get("account/features").pipe(
      map((flags: Partial<FeatureFlags> | null | undefined) => ({
        emotionTransfer: flags?.emotionTransfer === true,
        emotionTransferFeatureEnabled:
          flags?.emotionTransferFeatureEnabled === true,
      })),
      defaultIfEmpty(disabledFlags),
      catchError(() => of(disabledFlags)),
      // applied in every case (success or fallback) so the whole UI shows the same state
      tap((flags: FeatureFlags) =>
        this.userHelperService.updateCurrentUserFeatureFlags(flags)
      ),
      shareReplay(1)
    );
    return this.flags$;
  }

  public getFlags(): Observable<FeatureFlags> {
    return this.flags$ || this.refresh();
  }
}
